/**
 * TaskBasicFields Component
 *
 * Handles basic task information:
 * - Task Name
 * - Start Date / Due Date
 * - Request Type / Sub Type
 * - Link URL / Urgency
 * - Linked Project / Assignee (when project selected)
 *
 * Extracted from TaskForm.js for maintainability.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Input, Select, DatePicker, Badge } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import { startCase } from 'lodash';
import { taskTypes, urgencyTypes, urgencyColors } from '../../configs/task.config';
import '../../styles/index.scss';

const { Option } = Select;

const TaskBasicFields = ({
  form,
  level,
  windowWidth,
  currentTaskId,
  linkedProjectId,
  selectedProjectId,
  setSelectedProjectId,
  projects,
  projectTeamMembers,
  filteredSubTypes,
  isSubTypeDisabled,
  handleSubTypeChange,
  fetchProjects,
  setTaskDatePickerVisible,
  setTaskStartDatePickerVisible,
}) => {
  return (
    <Col className="task-basic-fields">
      <Card
        className="task-basic-fields__card"
        title={<Text className="task-basic-fields__card-title">TASK DETAILS</Text>}
      >
        {/* Row 1: Task Name, Start Date, Due Date */}
        <Row gutter={windowWidth < 768 ? 16 : 32}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="name"
              label={
                <div>
                  <div>Task Name</div>
                  <div className="task-basic-fields__label-hint">
                    The name you will identify this task by
                  </div>
                </div>
              }
              rules={[{ required: true, message: 'Task Name is Required' }]}
              colon={false}
            >
              <Input placeholder="Enter task name" className="custom-input" />
            </Form.Item>
          </Col>
          {level === 'PLATFORM' && (
            <Col xs={24} sm={24} md={6} lg={6}>
              <Form.Item
                name="startDate"
                label={
                  <div>
                    <div>Start Date</div>
                    <div className="task-basic-fields__label-hint">
                      Optional: Date when task starts
                    </div>
                  </div>
                }
                colon={false}
                layout="vertical"
                className="task-w--full"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const dueDate = getFieldValue('dueDate');
                      if (!value || !dueDate) {
                        return Promise.resolve();
                      }
                      const startMoment = moment(value);
                      const dueMoment = moment(dueDate);

                      if (startMoment.isSame(dueMoment, 'day')) {
                        return Promise.reject(new Error('Start date cannot be the same as due date'));
                      }
                      if (startMoment.isAfter(dueMoment)) {
                        return Promise.reject(new Error('Start date must be before due date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  onOpenChange={(open) => {
                    if (open) {
                      setTaskStartDatePickerVisible(true);
                    }
                  }}
                  className="custom-date task-w--full"
                  open={false}
                  suffixIcon={<CalendarOutlined />}
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={24} md={level === 'PLATFORM' ? 6 : 12} lg={level === 'PLATFORM' ? 6 : 12}>
            <Form.Item
              name="dueDate"
              label={
                <div>
                  <div>Due Date</div>
                  <div className="task-basic-fields__label-hint">
                    {level === 'PLATFORM' && selectedProjectId && 'Optional: '}Date of task completion
                  </div>
                </div>
              }
              colon={false}
              layout="vertical"
              className="task-w--full"
              rules={[
                {
                  required: !selectedProjectId,
                  message: 'Due Date is Required',
                },
                ...(level === 'PLATFORM' ? [
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue('startDate');
                      if (!startDate || !value) {
                        return Promise.resolve();
                      }
                      const startMoment = moment(startDate);
                      const dueMoment = moment(value);

                      if (dueMoment.isSame(startMoment, 'day')) {
                        return Promise.reject(new Error('Due date cannot be the same as start date'));
                      }
                      if (dueMoment.isBefore(startMoment)) {
                        return Promise.reject(new Error('Due date must be after start date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ] : []),
              ]}
            >
              <DatePicker
                onOpenChange={(open) => {
                  if (open) {
                    setTaskDatePickerVisible(true);
                  }
                }}
                className="custom-date task-w--full"
                open={false}
                suffixIcon={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: Request Type and Sub Type */}
        <Row gutter={32}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="requestType"
              label={
                <div>
                  <div>Request Type</div>
                  <div className="task-basic-fields__label-hint">
                    Select the type that best matches this task
                  </div>
                </div>
              }
              rules={[{ required: true, message: 'Request Type is Required' }]}
              colon={false}
            >
              <Select
                placeholder="Select request type"
                onChange={handleSubTypeChange}
                className="custom-select"
              >
                {taskTypes.map((type, index) => (
                  <Option key={index} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="requestSubType"
              colon={false}
              label={
                <div>
                  <div>Request Details</div>
                  <div className="task-basic-fields__label-hint">
                    Optional: Select a sub-type that best matches this task
                  </div>
                </div>
              }
            >
              <Select
                placeholder="Select request details"
                disabled={isSubTypeDisabled}
                className="custom-select"
              >
                {filteredSubTypes.map((subtype, index) => (
                  <Option key={index} value={subtype}>
                    {subtype}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Link URL and Urgency */}
        <Row gutter={32}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="linkUrl"
              label={
                <div>
                  <div>Link</div>
                  <div className="task-basic-fields__label-hint">
                    Optional: Attach one link to the task
                  </div>
                </div>
              }
              colon={false}
              rules={[
                {
                  max: 499,
                  message: 'Cannot be any longer than 500 characters',
                },
              ]}
            >
              <Input placeholder="Enter link" maxLength={500} className="custom-input" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="urgency"
              label={
                <div>
                  <div>Urgency</div>
                  <div className="task-basic-fields__label-hint">
                    Optional: Set the urgency or priority level of this task
                  </div>
                </div>
              }
              colon={false}
            >
              <Select placeholder="Select urgency type" className="custom-select">
                {Object.keys(urgencyTypes).map((urgency) => (
                  <Option key={urgency} value={urgencyTypes[urgency]}>
                    <Badge
                      color={urgencyColors[urgencyTypes[urgency]]}
                      className="task-basic-fields__urgency-badge"
                    />
                    {urgency}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Row 5: Linked Project and Assignee (Platform only) */}
        {level === 'PLATFORM' && (
          <Row gutter={32}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="linkedProjectId"
                label={
                  <div>
                    <div>Linked Project</div>
                    <div className="task-basic-fields__label-hint">
                      Optional: Link this task to a project
                    </div>
                  </div>
                }
                colon={false}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a project"
                  disabled={linkedProjectId && currentTaskId === 'new'}
                  onFocus={() => fetchProjects()}
                  onSearch={(searchText) => fetchProjects(searchText)}
                  onChange={(value) => {
                    setSelectedProjectId(value);
                    form.setFieldsValue({ assigneeUserId: [] });
                    if (value) {
                      form.setFieldsValue({ dueDate: null });
                    }
                    form.validateFields(['dueDate']);
                  }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  className="custom-select"
                >
                  {projects.map((project) => (
                    <Option key={project.id} value={project.id}>
                      {project.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {selectedProjectId && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  name="assigneeUserId"
                  label={
                    <div>
                      <div>Assignee</div>
                      <div className="task-basic-fields__label-hint">
                        Select from project owner and team members
                      </div>
                    </div>
                  }
                  colon={false}
                >
                  <Select
                    placeholder="Select Assignee"
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    filterSort={(optionA, optionB) =>
                      optionA.children
                        .toLowerCase()
                        .localeCompare(optionB.children.toLowerCase())
                    }
                    className="custom-select"
                  >
                    {projectTeamMembers.map((user) => (
                      <Option key={user.id} value={user.id}>
                        {startCase(user.name || user.username)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
        )}
      </Card>
    </Col>
  );
};

TaskBasicFields.propTypes = {
  form: PropTypes.object.isRequired,
  level: PropTypes.string,
  windowWidth: PropTypes.number.isRequired,
  currentTaskId: PropTypes.string,
  linkedProjectId: PropTypes.string,
  selectedProjectId: PropTypes.string,
  setSelectedProjectId: PropTypes.func.isRequired,
  projects: PropTypes.array,
  projectTeamMembers: PropTypes.array,
  filteredSubTypes: PropTypes.array,
  isSubTypeDisabled: PropTypes.bool,
  handleSubTypeChange: PropTypes.func.isRequired,
  fetchProjects: PropTypes.func.isRequired,
  setTaskDatePickerVisible: PropTypes.func.isRequired,
  setTaskStartDatePickerVisible: PropTypes.func.isRequired,
};

TaskBasicFields.defaultProps = {
  level: null,
  currentTaskId: null,
  linkedProjectId: null,
  selectedProjectId: null,
  projects: [],
  projectTeamMembers: [],
  filteredSubTypes: [],
  isSubTypeDisabled: false,
};

export default TaskBasicFields;
